<script setup lang="ts">
import { Order } from '@/api/order/types';
import RecapSvg from '@/assets/drawings/recap.svg?component';
import { Environment } from '@/enums/environment.enum';
import { APK } from '@/interfaces/deal.interface';
import { EyesoftProperties, InstallProperties, KrysProperties, MiaProperties, OculeraProperties } from '@/interfaces/properties.interface';
import { StateMachineEvent, StateMachineSnapshot } from '@/stateMachine';
import { VueformElement } from '@vueform/vueform';
import { Ref, ref } from 'vue';

interface FormData {
  procedure: APK;
  environment: Environment;
  preview: boolean;
  eyesoft_code_client: string | null;
  krys_client_code: string | null;
  krys_wifi_password: string | null;
  krys_wifi_ssid: string | null;
  oculera_verson: string | null;
}

const props = defineProps<{
  snapshot: StateMachineSnapshot;
  send: (event: StateMachineEvent) => void;
}>();

const form$: Ref<VueformElement | null> = ref(null);

const updateProperties = async (data: FormData) => {
  const properties: InstallProperties = {
    procedure: data.procedure as APK,
    environment: data.environment,
    preview: data.preview,
    properties: data as EyesoftProperties | KrysProperties | OculeraProperties | MiaProperties,
  };
  const order: Order = {
    id: -1,
    clientEmail: 'custom',
    assignedTo: [],
    isFulfilled: false,
    createdBy: {
      id: -1,
      email: 'custom',
      isAdmin: false,
    },
    procedure: properties,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    installedAt: null,
    installedBy: null,
    installedHeadsetSerial: null,
  };
  props.send({
    type: 'order.found',
    value: order,
  });
  props.send({
    type: 'properties.set',
    value: properties,
  });
};
</script>

<template>
  <div class="d-flex h-100">
    <div class="flex-grow-1 text-center d-flex justify-content-center flex-column align-items-center m-3">
      <h2 class="mb-3">Personalisation</h2>
      <div class="w-100">
        <Vueform ref="form$" size="md" :display-errors="false" :endpoint="false" @change="updateProperties">
          <SelectElement
            name="procedure"
            :items="[
              {
                value: 'Opticien',
                label: 'Opticien',
              },
              {
                value: 'Ortho',
                label: 'Ortho',
              },
              {
                value: 'Ortho homme',
                label: 'Ortho Home',
              },
              {
                value: 'Oculera',
                label: 'Oculera',
              },
              {
                value: 'Krys',
                label: 'Krys',
              },
              {
                value: 'CDL',
                label: 'CDL',
              },
              {
                value: 'MIA',
                label: 'MIA',
              },
            ]"
            :search="true"
            :native="false"
            label="Procédure"
            input-type="search"
            autocomplete="off"
          />
          <SelectElement
            name="environment"
            :items="[
              {
                value: 'prod',
                label: 'Prod',
              },
              {
                value: 'beta',
                label: 'Beta',
              },
              {
                value: 'recette',
                label: 'Recette',
              },
            ]"
            :search="true"
            :native="false"
            label="Environement"
            input-type="search"
            autocomplete="off"
          />
          <ToggleElement name="preview"> Mode preview </ToggleElement>
          <GroupElement name="krys" :conditions="[['procedure', 'in', ['Krys', 'CDL']]]">
            <TextElement name="krys_client_code" label="Code Client" :rules="['regex:/^\\d{6}$/m']" />
            <TextElement name="krys_wifi_ssid" label="WiFI SSID" />
            <TextElement name="krys_wifi_password" input-type="password" label="WiFi Password" />
          </GroupElement>
          <GroupElement name="eyesoft" :conditions="[['procedure', 'in', ['Opticien', 'Ortho']]]">
            <TextElement name="eyesoft_code_client" label="Code Client" />
          </GroupElement>
          <GroupElement name="oculera" :conditions="[['procedure', 'in', ['Oculera']]]">
            <SelectElement
              name="oculera_verson"
              :items="['2.3.0', '2.6.4', 'latest']"
              default="latest"
              :search="true"
              :native="false"
              label="Version"
              input-type="search"
              autocomplete="off"
            />
          </GroupElement>
          <GroupElement name="mia" :conditions="[['procedure', 'in', ['MIA']]]">
            <SelectElement
              name="product"
              :rules="['required']"
              :items="[
                {
                  value: 'evosight',
                  label: 'Evosight',
                },
                {
                  value: 'visionlab',
                  label: 'VisionLab',
                },
                {
                  value: 'opticien',
                  label: 'Check Lenscape',
                },
              ]"
              :search="true"
              :native="false"
              label="Product"
              input-type="search"
              autocomplete="off"
              default="evosight"
            />
            <TextElement name="mia_sender_bundle_id" label="Sender Bundle ID" expression="{MIA_BUNDLE_ID(product)}" readonly />
            <TextElement name="mia_product_small_name" label="Product Small Name" expression="{product}" readonly />
            <TextElement
              name="mia_api_uri"
              label="API URI"
              expression="{MIA_API(product, environment)}"
              readonly
            />
            <TextElement name="mia_target_pui_version" label="Target PUI Version" default="5.9.9.0" />
            <TextElement name="mia_language" label="Language" default="fr" />
          </GroupElement>
        </Vueform>
      </div>
    </div>
    <div class="d-flex justify-content-center flex-column align-items-center w-50 m-3">
      <RecapSvg />
    </div>
  </div>
</template>

<style scoped></style>
